import React, { Component } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { debounce, isEmpty as empty } from 'lodash';
import { parse as qsParse, stringify as qsStringify } from 'query-string';
import { TableSortIcon } from '@spotify-internal/encore-creator-web';
import { scroller as scroll } from 'react-scroll';
import { Pagination } from '../../Shared/Pagination';
import { ConnectedFilterWithDatePicker } from '../../Shared/FilterWithDatePicker';
import { LoadingIndicator } from '../../Shared/LoadingIndicator';
import styles from './index.module.scss';
import {
  LOADING,
  ERROR,
  selectEpisodeList,
  selectEpisodeListStatus,
  selectDateRange,
} from '../../../store/selectors';
import { loadEpisodeList, unsetEpisodeList } from '../../../store/episodeList';
import { downloadEpisodeListAsCsv } from '../../../store/downloadCsv';
import { trackEvent } from '../../../lib/Tracking';
import { Table, TableRow, TableCell, TableThumbnail } from '@spotify-internal/encore-web';
import { ReduxState, PaginatedEpisodes, DateRangeWithLabel, Episode } from '../../../types';
import { screenMdMin, black } from '@spotify-internal/encore-foundation';
import { TableHeaderCell, TableHeaderLabel, TableRowLink } from '../../Shared/Table';
import { injectIntl, IntlShape, defineMessage } from 'react-intl';

type Props = {
  podcastId: string;

  status?: 'LOADING' | 'ERROR';
  episodeList?: PaginatedEpisodes;
  dateRange?: DateRangeWithLabel;

  loadEpisodeList: typeof loadEpisodeList;
  downloadEpisodeListAsCsv: typeof downloadEpisodeListAsCsv;
  unsetEpisodeList: typeof unsetEpisodeList;
  intl: IntlShape;
} & RouteComponentProps;

type State = {
  sortBy: string;
  sortDescending: boolean;
  isMobile: boolean;
};

const MediaQueryList = window.matchMedia(`(min-width: ${screenMdMin})`);

export class InternationalizedEpisodeList extends Component<Props, State> {
  state: State = {
    sortBy: 'releaseDate',
    sortDescending: true,
    isMobile: !MediaQueryList.matches,
  };

  matchMediaListener =
    /* istanbul ignore next */
    (event: MediaQueryListEvent) => {
      /* istanbul ignore next */
      this.setState({ isMobile: !event.matches });
    };

  componentDidMount() {
    this.loadEpisodeList();
    MediaQueryList.addListener(this.matchMediaListener);
  }

  /* istanbul ignore next */
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!this.props.dateRange) return;

    if (
      nextProps.dateRange.startDate !== this.props.dateRange.startDate ||
      nextProps.dateRange.endDate !== this.props.dateRange.endDate ||
      nextProps.dateRange.label !== this.props.dateRange.label
    ) {
      this.props.loadEpisodeList(
        this.props.podcastId,
        this.searchQuery,
        this.currentPage,
        this.state.sortBy,
        this.state.sortDescending,
      );
    }
  }

  /* istanbul ignore next */
  componentDidUpdate() {
    if (this.props.episodeList && this.props.episodeList.currentPage !== this.currentPage) {
      this.props.loadEpisodeList(
        this.props.podcastId,
        this.searchQuery as string,
        this.currentPage,
        this.state.sortBy,
        this.state.sortDescending,
      );
      scroll.scrollTo('filter-with-date-picker');
    }
  }

  /* istanbul ignore next */
  componentWillUnmount() {
    this.props.unsetEpisodeList();
    MediaQueryList.removeListener(this.matchMediaListener);
  }

  get queryParams() {
    return qsParse(this.props.location.search);
  }

  get searchQuery() {
    return (this.queryParams.search as string) || '';
  }

  get currentPage() {
    return parseInt(this.queryParams.page as string, 10) || 1;
  }

  formatValueByDataAvailability(field: string, episode: Episode) {
    const { intl } = this.props;
    if (!this.props.dateRange) return null;

    return intl.formatNumber(episode[field]);
  }

  loadEpisodeList() {
    this.props.loadEpisodeList(
      this.props.podcastId,
      this.searchQuery as string,
      this.currentPage,
      this.state.sortBy,
      this.state.sortDescending,
    );
  }

  filterEpisodeList = debounce(
    /* istanbul ignore next */
    () => {
      this.props.loadEpisodeList(
        this.props.podcastId,
        this.searchQuery as string,
        this.currentPage,
        this.state.sortBy,
        this.state.sortDescending,
      );
    },
    500,
  );

  formatQueryParams = (params: { [key: string]: any }) => {
    return qsStringify({ ...this.queryParams, ...params });
  };

  handleSearchInput() {
    return (query: string) => {
      const { history, location } = this.props;
      const isEmptyQuery = empty(query.trim());

      this.filterEpisodeList();
      trackEvent({ category: 'episode', action: 'search', label: query });

      /* istanbul ignore next */
      const queryParams = this.formatQueryParams({
        search: isEmptyQuery ? undefined : query,
        page: 1,
      });
      /* istanbul ignore next */
      history.replace(`${location.pathname}?${queryParams}${location.hash || ''}`);
    };
  }

  handleTableRowClick = (episodeId: string) => () =>
    this.props.history.push(`/episode/${episodeId}`);

  handleColumnSort = (columnKey: string) => () => {
    let sortDescending = true;
    /* istanbul ignore else */
    if (columnKey === this.state.sortBy) {
      sortDescending = !this.state.sortDescending;
    }
    trackEvent({ category: 'episode-list', action: 'sort', label: columnKey });
    this.setState({ sortBy: columnKey, sortDescending }, this.loadEpisodeList);
  };

  handlePageChange() {
    return ({ selected }) => {
      const { history, location } = this.props;
      const queryParams = this.formatQueryParams({ page: selected });

      history.push(`${location.pathname}?${queryParams}${location.hash || ''}`);
      trackEvent({ category: 'episode-list', action: 'page-change', label: selected });
      scroll.scrollTo('filter-with-date-picker');

      return this.props.loadEpisodeList(
        this.props.podcastId,
        this.searchQuery,
        selected,
        this.state.sortBy,
        this.state.sortDescending,
      );
    };
  }

  handleCsvDownload = /* istanbul ignore next */ () => {
    this.props.downloadEpisodeListAsCsv(
      this.props.podcastId,
      this.searchQuery,
      this.state.sortBy,
      this.state.sortDescending,
    );
  };

  renderColumnSortIcon = (columnKey: string) => {
    if (columnKey === this.state.sortBy) {
      return <TableSortIcon direction={this.state.sortDescending ? 'down' : 'up'} />;
    }
    return null;
  };

  renderColGroup = () => {
    const isDesktop = !this.state.isMobile;

    /* istanbul ignore else */
    if (isDesktop) {
      return (
        <colgroup>
          <col />
          <col width="100" />
          <col width="100" />
          <col width="100" />
          <col width="150" />
        </colgroup>
      );
    }

    /* istanbul ignore next */
    return (
      <colgroup>
        <col />
        <col width="100" />
        <col width="150" />
      </colgroup>
    );
  };

  renderTableHead = () => {
    const { intl, episodeList } = this.props;
    const isDesktop = !this.state.isMobile;
    const epMsg = defineMessage({
      id: 'episode-list.header.episode',
      defaultMessage: `{episodeCount, plural,
        one {# episode}
        other {# episodes}
      }`,
    });
    const startsMsg = defineMessage({
      id: 'episode-list.header.starts',
      defaultMessage: 'Starts',
    });
    const streamsMsg = defineMessage({
      id: 'episode-list.header.streams',
      defaultMessage: 'Streams',
    });
    const listenersMsg = defineMessage({
      id: 'episode-list.header.listeners',
      defaultMessage: 'Listeners',
    });
    const publishDateMsg = defineMessage({
      id: 'episode-list.header.publish-date',
      defaultMessage: 'Publish Date',
    });
    const episodesHeader = intl.formatMessage(epMsg, { episodeCount: episodeList!.totalEpisodes });
    const startsHeader = intl.formatMessage(startsMsg);
    const streamsHeader = intl.formatMessage(streamsMsg);
    const listenersHeader = intl.formatMessage(listenersMsg);
    const publishDateHeader = intl.formatMessage(publishDateMsg);

    return (
      <TableRow>
        <TableHeaderCell onClick={this.handleColumnSort('name')} style={{ paddingLeft: 0 }}>
          <TableHeaderLabel
            style={{ justifyContent: 'flex-start' }}
            active={this.state.sortBy === 'name'}
            label={episodesHeader}
          >
            {this.renderColumnSortIcon('name')}
          </TableHeaderLabel>
        </TableHeaderCell>
        <TableHeaderCell onClick={this.handleColumnSort('starts')}>
          <TableHeaderLabel active={this.state.sortBy === 'starts'} label={startsHeader}>
            {this.renderColumnSortIcon('starts')}
          </TableHeaderLabel>
        </TableHeaderCell>
        {isDesktop && (
          <React.Fragment>
            <TableHeaderCell onClick={this.handleColumnSort('streams')}>
              <TableHeaderLabel active={this.state.sortBy === 'streams'} label={streamsHeader}>
                {this.renderColumnSortIcon('streams')}
              </TableHeaderLabel>
            </TableHeaderCell>
            <TableHeaderCell onClick={this.handleColumnSort('listeners')}>
              <TableHeaderLabel active={this.state.sortBy === 'listeners'} label={listenersHeader}>
                {this.renderColumnSortIcon('listeners')}
              </TableHeaderLabel>
            </TableHeaderCell>
          </React.Fragment>
        )}
        <TableHeaderCell onClick={this.handleColumnSort('releaseDate')}>
          <TableHeaderLabel active={this.state.sortBy === 'releaseDate'} label={publishDateHeader}>
            {this.renderColumnSortIcon('releaseDate')}
          </TableHeaderLabel>
        </TableHeaderCell>
      </TableRow>
    );
  };

  renderTableBody() {
    const { intl } = this.props;
    const isDesktop = !this.state.isMobile;

    return this.props.episodeList!.episodes.map(episode => (
      <TableRow
        data-testid="table-row"
        key={episode.id}
        hover
        onClick={this.handleTableRowClick(episode.id)}
        style={{ cursor: 'pointer' }}
      >
        <TableCell>
          <TableThumbnail
            img={episode.artworkUrl}
            imgAlt=""
            thumbnailTitle={
              <TableRowLink to={`/episode/${episode.id}`}>{episode.name}</TableRowLink>
            }
            small
          />
        </TableCell>
        <TableCell align="right" numerical>
          {this.formatValueByDataAvailability('starts', episode)}
        </TableCell>
        {isDesktop && (
          <React.Fragment>
            <TableCell align="right" numerical>
              {this.formatValueByDataAvailability('streams', episode)}
            </TableCell>
            <TableCell align="right" numerical>
              {this.formatValueByDataAvailability('listeners', episode)}
            </TableCell>
          </React.Fragment>
        )}
        <TableCell align="right">
          {intl.formatDate(episode.releaseDate, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })}
        </TableCell>
      </TableRow>
    ));
  }

  renderLoader = () => (
    <div className={styles.loader}>
      <LoadingIndicator style={{ height: '35vh' }} />
    </div>
  );

  render() {
    if (this.props.status === ERROR) {
      return <span data-qa="episode-list-error" />;
    }

    if (!this.props.episodeList && this.props.status !== LOADING) {
      return null;
    }

    if (!this.props.episodeList && this.props.status === LOADING) {
      return (
        <div data-qa="episode-list-loading" className={styles.container}>
          {this.renderLoader()}
        </div>
      );
    }

    return (
      <div data-qa="episode-list" className={styles.container}>
        {/* istanbul ignore next */ this.props.status === LOADING && this.renderLoader()}
        <ConnectedFilterWithDatePicker
          downloadDataAsCsv={this.handleCsvDownload}
          onUserInput={this.handleSearchInput()}
          csvDownloadTrackingLabel="episode-list"
          value={this.searchQuery}
          showFirstDaysOptions
        />
        <Table>
          {this.renderColGroup()}
          <thead>{this.renderTableHead()}</thead>
          <tbody>{this.renderTableBody()}</tbody>
        </Table>
        <Pagination
          onPageChangeHandler={this.handlePageChange()}
          pageCount={this.props.episodeList!.totalPages}
        />
      </div>
    );
  }
}

function mapStateToProps(state: ReduxState) {
  return {
    episodeList: selectEpisodeList(state),
    status: selectEpisodeListStatus(state),
    dateRange: selectDateRange(state),
  };
}

const actions = { loadEpisodeList, unsetEpisodeList, downloadEpisodeListAsCsv };

const EpisodeList = injectIntl(InternationalizedEpisodeList);
export const ConnectedEpisodeList = connect(mapStateToProps, actions)(EpisodeList);
